import type {
    BrowserRenderService,
    CacheStore,
    PreparedReceipt,
    RenderRequest,
    SyncResult,
    TemplateClient,
    WriteOperation
} from '../../receipt-renderer/index.js'

type ReceiptSdk = typeof import('../../receipt-renderer/index.js')

export interface ReceiptRendererConfig {
    registryUrl: string
    registryToken: string
    channel: string
    templateIds: string[]
    baseUrl: string
}

export function readReceiptRendererConfig(
    env: Record<string, string | boolean | undefined>
): ReceiptRendererConfig | null {
    const read = (key: string) =>
        typeof env[key] === 'string' ? env[key].trim() : ''
    const registryUrl = read('VITE_RECEIPT_REGISTRY_URL')
    const registryToken = read('VITE_RECEIPT_REGISTRY_TOKEN')
    if (!registryUrl && !registryToken) return null
    if (!registryUrl || !registryToken) {
        throw new Error(
            'Set both VITE_RECEIPT_REGISTRY_URL and VITE_RECEIPT_REGISTRY_TOKEN'
        )
    }

    let url: URL
    try {
        url = new URL(registryUrl)
    } catch {
        throw new Error(
            'VITE_RECEIPT_REGISTRY_URL must be an absolute HTTP(S) URL'
        )
    }
    if (
        !['https:', 'http:'].includes(url.protocol) ||
        url.username ||
        url.password ||
        url.search ||
        url.hash
    ) {
        throw new Error(
            'VITE_RECEIPT_REGISTRY_URL must be HTTP(S), without credentials, query or fragment'
        )
    }

    return {
        registryUrl: url.href.replace(/\/+$/, ''),
        registryToken,
        channel: read('VITE_RECEIPT_REGISTRY_CHANNEL') || 'stable',
        templateIds: [
            ...new Set(
                read('VITE_RECEIPT_TEMPLATE_IDS')
                    .split(',')
                    .map((id) => id.trim())
                    .filter(Boolean)
            )
        ],
        baseUrl: read('BASE_URL') || '/'
    }
}

// The SDK's default database keys only contain template IDs. Isolate registry,
// channel and runtime credentials so a config change cannot reuse another
// deployment's templates. Only a hash of the scope is stored in the keys.
async function scopeStore(
    store: CacheStore,
    config: ReceiptRendererConfig
): Promise<CacheStore> {
    const scope = new TextEncoder().encode(
        JSON.stringify([
            config.registryUrl,
            config.channel,
            config.registryToken
        ])
    )
    const digest = await crypto.subtle.digest('SHA-256', scope)
    const prefix =
        Array.from(new Uint8Array(digest), (byte) =>
            byte.toString(16).padStart(2, '0')
        ).join('') + ':'
    return {
        persistent: store.persistent,
        get: (name, key) => store.get(name, prefix + key),
        writeMany: (operations: WriteOperation[]) =>
            store.writeMany(
                operations.map((operation) => ({
                    ...operation,
                    key: prefix + operation.key
                }))
            ),
        keys: async (name) =>
            (await store.keys(name))
                .filter((key) => key.startsWith(prefix))
                .map((key) => key.slice(prefix.length)),
        close: () => store.close()
    }
}

interface RegistrySession {
    store: CacheStore
    templates: TemplateClient
}

export class ReceiptRendererService {
    private sdk?: Promise<ReceiptSdk>
    private session?: Promise<RegistrySession>
    private renderer?: Promise<BrowserRenderService>
    private pendingSyncs = new Map<string, Promise<SyncResult>>()
    private prepared = new Map<
        string,
        { digest: string; value: Promise<PreparedReceipt> }
    >()
    private used = new Set<string>()
    private disposed = false

    constructor(
        private readonly config: ReceiptRendererConfig,
        private readonly loadSdk: () => Promise<ReceiptSdk> = () =>
            import('../../receipt-renderer/index.js')
    ) {}

    private assertOpen() {
        if (this.disposed) throw new Error('Receipt renderer has been disposed')
    }

    private getSdk() {
        this.assertOpen()
        return (this.sdk ??= this.loadSdk().catch((error) => {
            this.sdk = undefined
            throw error
        }))
    }

    private getSession(): Promise<RegistrySession> {
        this.assertOpen()
        return (this.session ??= this.getSdk()
            .then(async (sdk) => {
                const rawStore = await sdk.IndexedDbCacheStore.open()
                try {
                    const store = await scopeStore(rawStore, this.config)
                    this.assertOpen()
                    const transport = new sdk.HttpRegistryTransport(
                        this.config.registryUrl,
                        this.config.registryToken,
                        (input, init) =>
                            fetch(input, {
                                ...init,
                                cache: 'no-store',
                                signal: AbortSignal.timeout(10_000)
                            })
                    )
                    return {
                        store,
                        templates: new sdk.TemplateClient({
                            store,
                            transport,
                            engineVersion: sdk.ENGINE_VERSION,
                            channel: this.config.channel
                        })
                    }
                } catch (error) {
                    rawStore.close()
                    throw error
                }
            })
            .catch((error) => {
                this.session = undefined
                throw error
            }))
    }

    private getRenderer(): Promise<BrowserRenderService> {
        this.assertOpen()
        return (this.renderer ??= this.getSdk()
            .then(async (sdk) => {
                const renderer = await sdk.createRenderService({
                    baseUrl: this.config.baseUrl
                })
                if (this.disposed) {
                    renderer.dispose()
                    this.assertOpen()
                }
                return renderer
            })
            .catch((error) => {
                this.renderer = undefined
                throw error
            }))
    }

    async syncTemplate(templateId: string): Promise<SyncResult> {
        this.assertOpen()
        if (!templateId.trim())
            throw new Error('A published template ID is required')
        this.used.add(templateId)
        const pending = this.pendingSyncs.get(templateId)
        if (pending) return pending
        const sync = this.getSession()
            .then(async ({ store, templates }) => {
                // Drop a damaged activation pointer so the next request downloads
                // the package again instead of receiving an unhelpful HTTP 304.
                if ((await templates.status(templateId)).state === 'corrupt') {
                    await store.writeMany([
                        { store: 'activations', key: templateId }
                    ])
                }
                return templates.sync(templateId)
            })
            .catch(
                (): SyncResult => ({
                    status: 'unavailable',
                    reason: 'Receipt registry synchronization failed'
                })
            )
            .finally(() => this.pendingSyncs.delete(templateId))
        this.pendingSyncs.set(templateId, sync)
        return sync
    }

    async syncKnownTemplates() {
        const { templates } = await this.getSession()
        const cached = await templates.listCached()
        const ids = new Set([
            ...this.config.templateIds,
            ...this.used,
            ...cached.map((entry) => entry.templateId)
        ])
        return Promise.all(
            [...ids].map(async (templateId) => ({
                templateId,
                result: await this.syncTemplate(templateId)
            }))
        )
    }

    async listTemplates(extraIds: string[] = []) {
        const { templates } = await this.getSession()
        const cached = await templates.listCached()
        const ids = new Set([
            ...this.config.templateIds,
            ...extraIds,
            ...this.used,
            ...cached.map((entry) => entry.templateId)
        ])
        return Promise.all(
            [...ids].map(async (templateId) => {
                const active = await templates.getActive(templateId)
                return {
                    templateId,
                    name: active?.pkg.manifest.name ?? templateId
                }
            })
        )
    }

    async loadTemplate(templateId: string) {
        const result = await this.syncTemplate(templateId)
        const { templates } = await this.getSession()
        const active =
            (await templates.status(templateId)).state === 'offline-ready'
                ? await templates.getActive(templateId)
                : null
        if (!active)
            throw new Error(`Receipt template unavailable (${result.status})`)
        return { ...active, sync: result }
    }

    async renderTemplate(templateId: string, request: RenderRequest) {
        this.assertOpen()
        if (!templateId.trim())
            throw new Error('A published template ID is required')
        request.signal?.throwIfAborted()
        this.used.add(templateId)
        const { templates } = await this.getSession()
        let active =
            (await templates.status(templateId)).state === 'offline-ready'
                ? await templates.getActive(templateId)
                : null

        if (!active) {
            const sync = await this.syncTemplate(templateId)
            active =
                (await templates.status(templateId)).state === 'offline-ready'
                    ? await templates.getActive(templateId)
                    : null
            if (!active)
                throw new Error(`Receipt template unavailable (${sync.status})`)
        }
        // Pin this verified revision for the job. Startup/online synchronization
        // updates later jobs without putting network requests on cached prints.
        request.signal?.throwIfAborted()
        const renderer = await this.getRenderer()
        let prepared = this.prepared.get(templateId)
        if (!prepared || prepared.digest !== active.digest) {
            const value = renderer.prepare(active.pkg)
            prepared = { digest: active.digest, value }
            this.prepared.set(templateId, prepared)
            void value.catch(() => {
                if (this.prepared.get(templateId)?.value === value)
                    this.prepared.delete(templateId)
            })
        }
        const template = await prepared.value
        this.assertOpen()
        request.signal?.throwIfAborted()
        const result = await renderer.render(template, {
            ...request,
            language: request.language ?? 'sr',
            context: {
                locale: 'sr-RS',
                timezone: 'Europe/Belgrade',
                ...request.context
            }
        }).promise
        const sdk = await this.getSdk()
        const bmpBytes = sdk.encodeBmp1(
            result.raster,
            request.profile.dpi.horizontal
        )
        return {
            ...result,
            bmpBytes,
            bmpDataUrl: `data:image/bmp;base64,${sdk.bytesToBase64(bmpBytes)}`
        }
    }

    dispose() {
        if (this.disposed) return
        this.disposed = true
        void this.renderer
            ?.then((renderer) => renderer.dispose())
            .catch(() => {})
        void this.session?.then(({ store }) => store.close()).catch(() => {})
        this.prepared.clear()
    }
}

let sharedRenderer: ReceiptRendererService | undefined

export function getReceiptRenderer(): ReceiptRendererService {
    if (sharedRenderer) return sharedRenderer
    const config = readReceiptRendererConfig(import.meta.env)
    if (!config) throw new Error('Receipt registry is not configured')
    return (sharedRenderer = new ReceiptRendererService(config))
}

// Called once by app startup; no renderer is created on React mounts.
export function startReceiptRenderer(): () => void {
    let service: ReceiptRendererService
    try {
        if (!readReceiptRendererConfig(import.meta.env)) return () => {}
        service = getReceiptRenderer()
    } catch {
        console.warn('Receipt registry configuration is incomplete or invalid')
        return () => {}
    }
    const sync = () => {
        void service
            .syncKnownTemplates()
            .then((results) => {
                for (const { templateId, result } of results) {
                    if (
                        result.status !== 'updated' &&
                        result.status !== 'up-to-date'
                    ) {
                        console.warn(
                            'Receipt template sync:',
                            templateId,
                            result.status
                        )
                    }
                }
            })
            .catch(() => console.warn('Receipt template cache is unavailable'))
    }
    const stop = () => {
        window.removeEventListener('online', sync)
        window.removeEventListener('pagehide', onPageHide)
        service.dispose()
        if (sharedRenderer === service) sharedRenderer = undefined
    }
    const onPageHide = (event: PageTransitionEvent) => {
        if (!event.persisted) stop()
    }
    window.addEventListener('online', sync)
    window.addEventListener('pagehide', onPageHide)
    sync()
    return stop
}
